import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { can, createBook, listLanguages, listOrgBooks, publishBook, type Book } from "../api";
import BookCard from "../components/BookCard";
import SheetHeader from "../components/SheetHeader";
import LanguagePicker from "../studio/LanguagePicker";
import { useT } from "../i18n";
import { primaryOrg, useSession } from "../session";

/**
 * Studio is the creator's view: everything in their org, drafts included.
 *
 * What it renders is decided by the permission set the server sent with the
 * session, never by the role name. The server checks again on every call — this
 * is about not offering a button that would 403.
 */
export default function Studio() {
  const { me, isPending: sessionPending } = useSession();
  const org = primaryOrg(me);
  const client = useQueryClient();
  const navigate = useNavigate();
  const t = useT();

  const books = useQuery({
    queryKey: ["books", "org", org?.slug],
    queryFn: () => listOrgBooks(org!.slug),
    enabled: !!org,
  });

  /**
   * A new book is a button, not a text box standing at the top of the list.
   *
   * Starting a book is occasional; the list is what this page is for, and a
   * permanent input made the first thing on it a form. Creating also goes
   * straight to the book, because the next thing anybody does with a new book is
   * add its chapters — and that happens there, not here.
   */
  const [creating, setCreating] = useState(false);
  const create = useMutation({
    mutationFn: (v: { title: string; language: string }) =>
      createBook(org!.slug, { title: v.title, language: v.language }),
    onSuccess: async (book) => {
      await client.invalidateQueries({ queryKey: ["books"] });
      navigate(`/studio/books/${book.id}`);
    },
  });

  // The card only unpublishes. Publishing asks who can read the book first, so
  // the card's Publish leads to the edition panel instead of making a book
  // public in one tap (owner, 2026-10-24).
  const unpublish = useMutation({
    mutationFn: (id: string) => publishBook(id, false),
    onSuccess: () => client.invalidateQueries({ queryKey: ["books"] }),
  });

  if (sessionPending) {
    return <main className="mx-auto max-w-3xl px-4 py-10" aria-busy="true" />;
  }
  if (!me) {
    return <Navigate to="/" replace />;
  }
  if (!can(org, "book.read_draft")) {
    // Reached by typing the URL: the header does not offer the studio to an
    // account that has not been given it (ADR-0016). Said plainly, and without
    // implying something went wrong — this account works, it just reads.
    return (
      <main className="mx-auto max-w-3xl px-4 py-10">
        <h1 className="text-2xl font-semibold tracking-tight">{t.studio.title}</h1>
        <p data-testid="studio-forbidden" className="mt-2 text-sm text-muted">
          {t.studio.forbidden}
        </p>
        <Link
          to="/"
          className="mt-4 inline-block rounded-control border border-line px-3 py-1.5 text-sm transition-colors hover:bg-sunk"
        >
          {t.studio.backToLibrary}
        </Link>
      </main>
    );
  }

  const canCreate = can(org, "book.create");
  const empty = books.data?.items.length === 0;

  return (
    <main className="mx-auto max-w-3xl px-4 py-10">
      <header className="mb-8 flex items-center justify-between gap-3">
        <h1 className="min-w-0 truncate text-2xl font-semibold tracking-tight" dir="auto">
          {org?.name}
        </h1>
        {/* In the empty state the button is part of the invitation instead,
            so the page never offers the same action twice. */}
        <div className="flex shrink-0 items-center gap-2">
          {/* Who a book can be shared with (ADR-0153). */}
          {can(org, "group.manage") && (
            <Link
              to="/studio/groups"
              data-testid="studio-groups"
              className="rounded-control border border-line px-3 py-1.5 text-sm transition-colors hover:bg-sunk"
            >
              {t.studio.groups}
            </Link>
          )}
          {canCreate && books.data && !empty && <NewBookButton onClick={() => setCreating(true)} />}
        </div>
      </header>

      {empty && (
        <div
          data-testid="studio-empty"
          className="rounded-card border border-dashed border-line px-6 py-12 text-center"
        >
          <p className="text-sm text-muted">{t.studio.empty}</p>
          {canCreate && <NewBookButton className="mt-4" onClick={() => setCreating(true)} />}
        </div>
      )}

      {books.data && !empty && (
        <ul className="space-y-3" data-testid="studio-book-list">
          {books.data.items.map((book) => (
            <BookCard key={book.id} book={book} href={`/studio/books/${book.id}`}>
              {can(org, "book.publish") &&
                (book.published ? (
                  <button
                    type="button"
                    data-testid="unpublish"
                    onClick={() => unpublish.mutate(book.id)}
                    disabled={unpublish.isPending}
                    className="rounded-control border border-line px-3 py-1.5 text-xs transition-colors hover:bg-sunk disabled:opacity-50"
                  >
                    {t.studio.unpublish}
                  </button>
                ) : (
                  <Link
                    to={`/studio/books/${book.id}/publish`}
                    data-testid="publish"
                    className="rounded-control border border-line px-3 py-1.5 text-xs transition-colors hover:bg-sunk"
                  >
                    {t.studio.publish}
                  </Link>
                ))}
            </BookCard>
          ))}
        </ul>
      )}

      {creating && (
        <NewBookSheet
          books={books.data?.items ?? []}
          pending={create.isPending}
          error={
            create.isError
              ? create.error instanceof Error
                ? create.error.message
                : t.studio.createFailed
              : null
          }
          onCreate={(title, language) => create.mutate({ title, language })}
          onClose={() => {
            setCreating(false);
            create.reset();
          }}
        />
      )}
    </main>
  );
}

function NewBookButton({ onClick, className = "" }: { onClick: () => void; className?: string }) {
  const t = useT();
  return (
    <button
      type="button"
      data-testid="new-book"
      onClick={onClick}
      className={`inline-flex shrink-0 items-center gap-1.5 rounded-full bg-sage px-3.5 py-2 text-sm font-medium text-on-sage transition-colors hover:bg-sage-deep ${className}`}
    >
      <svg
        viewBox="0 0 24 24"
        className="size-4"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.2"
        strokeLinecap="round"
        aria-hidden="true"
      >
        <path d="M12 5v14M5 12h14" />
      </svg>
      {t.studio.newBook}
    </button>
  );
}

/**
 * NewBookSheet starts a book: blank, from the one thing a book needs to exist —
 * its title — or as a translation of one already in the library (requirement
 * 0018).
 */
function NewBookSheet({
  books,
  pending,
  error,
  onCreate,
  onClose,
}: {
  books: Book[];
  pending: boolean;
  error: string | null;
  onCreate: (title: string, language: string) => void;
  onClose: () => void;
}) {
  const t = useT();
  const [title, setTitle] = useState("");
  const [language, setLanguage] = useState("");
  const languages = useQuery({ queryKey: ["languages"], queryFn: listLanguages });
  return (
    <div
      role="dialog"
      aria-label={t.studio.newBook}
      data-testid="new-book-sheet"
      /* Above the header, like the other sheets. */
      className="fixed inset-x-0 bottom-0 z-40 rounded-t-card border-t border-line bg-surface shadow-[0_-8px_24px_-12px_rgb(0_0_0/0.25)]"
    >
      <div className="mx-auto w-full max-w-3xl">
        <SheetHeader title={t.studio.newBook} onClose={onClose} closeTestID="new-book-close" />
        {/* Translating is a screen, not a branch of this sheet (requirement
            0040): it has four sections, a scroll and a persistent action, and
            a bottom sheet is for a short focused choice. Blank stays, being two
            fields. */}
        {books.length > 0 && (
          <Link
            to="/studio/translate"
            data-testid="new-book-mode-translate"
            onClick={onClose}
            className="mx-4 mt-3 flex items-center justify-between gap-3 rounded-control border border-line px-3 py-2.5 text-sm transition-colors hover:bg-sunk"
          >
            <span className="font-medium">{t.translation.translate}</span>
            <span aria-hidden="true" className="text-muted rtl:rotate-180">
              →
            </span>
          </Link>
        )}
        <form
          data-testid="new-book-form"
          className="px-4 pt-3 pb-[max(1rem,env(safe-area-inset-bottom))]"
          onSubmit={(e) => {
            e.preventDefault();
            if (title.trim() && !pending) onCreate(title.trim(), language);
          }}
        >
          <input
            dir="auto"
            autoFocus
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={t.studio.newBookTitle}
            aria-label={t.studio.newBookTitle}
            enterKeyHint="done"
            data-testid="new-book-title"
            /* text-base: iOS zooms into any input under 16px on focus. */
            className="w-full rounded-control border border-line bg-surface px-3 py-2.5 text-base"
          />
          {/* The language, chosen with the title (ADR-0069). Everything the
                studio later buys about this book — its cast, its pictures, its
                cover — is written in it, and a book that has not said which
                gets a brief in one read off its title. Choosing here means the
                refusal is only ever met by a book that genuinely has nothing
                to judge by. It can still be changed in the book's settings. */}
          <div className="mt-3">
            <p className="mb-1.5 text-xs text-muted">{t.studio.newBookLanguage}</p>
            <LanguagePicker
              languages={languages.data?.items ?? []}
              value={language}
              onChoose={setLanguage}
              testID="new-book-language"
              disabled={pending}
            />
          </div>
          {error && (
            <p
              role="alert"
              data-testid="create-error"
              className="mt-2 text-sm text-red-600 dark:text-red-400"
            >
              {error}
            </p>
          )}
          <button
            type="submit"
            disabled={pending || !title.trim() || !language}
            data-testid="new-book-submit"
            className="mt-3 w-full rounded-control bg-sage px-4 py-2.5 text-sm font-medium text-on-sage transition-colors hover:bg-sage-deep disabled:opacity-40"
          >
            {t.studio.create}
          </button>
        </form>
      </div>
    </div>
  );
}
